import { AlertTriangle, Eye, EyeOff, Maximize2, Navigation, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type GoogleMapsLibrary, loadGoogleMaps } from '../../lib/google-maps-loader';
import { trpc } from '../../lib/trpc';
import type { Job, OperatorProfile, RouteAssignment } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

interface JobPlanMapProps {
  assignments: RouteAssignment[];
  unassignedJobs: Job[];
  operators: OperatorProfile[];
  onSelectJob?: (job: Job) => void;
  apiKey?: string;
}

interface GenericMapMarker {
  setMap: (map: unknown) => void;
  addListener: (event: string, handler: () => void) => void;
}

interface GenericPolyline {
  setMap: (map: unknown) => void;
}

interface GenericInfoWindow {
  setContent: (content: string) => void;
  open: (map: unknown, marker?: unknown) => void;
}

const OPERATOR_COLORS = [
  '#0284c7', // light blue
  '#10b981', // emerald
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#6366f1', // indigo
];

export function JobPlanMap({
  assignments,
  unassignedJobs,
  operators,
  onSelectJob,
  apiKey,
}: JobPlanMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const markersRef = useRef<GenericMapMarker[]>([]);
  const polylinesRef = useRef<GenericPolyline[]>([]);
  const infoWindowRef = useRef<GenericInfoWindow | null>(null);

  const [useGoogleMaps, setUseGoogleMaps] = useState<boolean | null>(null);
  const [_selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [activeOperatorFilter, setActiveOperatorFilter] = useState<number | null>(null);
  const [routeGeometries, setRouteGeometries] = useState<
    Map<number, Array<{ lat: number; lng: number }>>
  >(new Map());

  const trpcUtils = trpc.useUtils();

  // Fetch real road geometries from OSRM via backend for each operator route
  useEffect(() => {
    let isCancelled = false;

    async function fetchGeometries() {
      for (const assignment of assignments) {
        const opProfile = operators.find((o) => o.id === assignment.operatorProfileId);
        const waypoints: Array<{ lat: number; lng: number }> = [];

        if (opProfile?.startLocation?.latitude && opProfile.startLocation.longitude) {
          waypoints.push({
            lat: opProfile.startLocation.latitude,
            lng: opProfile.startLocation.longitude,
          });
        }

        const sortedStops = [...(assignment.stops || [])].sort((a, b) => a.position - b.position);
        for (const stop of sortedStops) {
          if (stop.job?.latitude != null && stop.job?.longitude != null) {
            waypoints.push({
              lat: stop.job.latitude,
              lng: stop.job.longitude,
            });
          }
        }

        if (opProfile?.endLocation?.latitude && opProfile.endLocation.longitude) {
          waypoints.push({
            lat: opProfile.endLocation.latitude,
            lng: opProfile.endLocation.longitude,
          });
        }

        if (waypoints.length >= 2) {
          try {
            const res = await trpcUtils.client.jobPlan.getRouteGeometry.query({
              coordinates: waypoints,
            });
            if (!isCancelled && res.success && res.coordinates.length > 0) {
              setRouteGeometries((prev) => {
                const next = new Map(prev);
                next.set(assignment.operatorProfileId, res.coordinates);
                return next;
              });
            }
          } catch {
            // Silently fall back to straight line waypoints
          }
        }
      }
    }

    fetchGeometries();

    return () => {
      isCancelled = true;
    };
  }, [assignments, operators, trpcUtils]);

  // Group operator colors
  const operatorColorMap = useMemo(() => {
    const map = new Map<number, string>();
    operators.forEach((op, idx) => {
      map.set(op.id, OPERATOR_COLORS[idx % OPERATOR_COLORS.length]);
    });
    return map;
  }, [operators]);

  // Extract all points with coordinates
  const placedUnassigned = useMemo(
    () => unassignedJobs.filter((j) => j.latitude != null && j.longitude != null),
    [unassignedJobs]
  );

  const missingCoordsCount = useMemo(
    () => unassignedJobs.filter((j) => j.latitude == null || j.longitude == null).length,
    [unassignedJobs]
  );

  const assignedStops = useMemo(() => {
    return assignments.flatMap((a) =>
      (a.stops || []).map((s) => ({
        ...s,
        operatorProfileId: a.operatorProfileId,
        color: operatorColorMap.get(a.operatorProfileId) || '#3b82f6',
      }))
    );
  }, [assignments, operatorColorMap]);

  // Depots from operators
  const depotLocations = useMemo(() => {
    const list: { id: number; label: string; lat: number; lng: number }[] = [];
    const seen = new Set<number>();

    operators.forEach((op) => {
      if (
        op.startLocation?.latitude &&
        op.startLocation?.longitude &&
        !seen.has(op.startLocation.id)
      ) {
        seen.add(op.startLocation.id);
        list.push({
          id: op.startLocation.id,
          label: op.startLocation.label,
          lat: op.startLocation.latitude,
          lng: op.startLocation.longitude,
        });
      }
    });
    return list;
  }, [operators]);

  // Initialize Google Maps or fallback
  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps(apiKey)
      .then((googleMaps: GoogleMapsLibrary) => {
        if (!isMounted || !mapContainerRef.current) return;
        setUseGoogleMaps(true);

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new googleMaps.Map(mapContainerRef.current, {
            center: { lat: -33.8688, lng: 151.2093 }, // Default Sydney
            zoom: 11,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          });
          infoWindowRef.current = new googleMaps.InfoWindow();
        }
      })
      .catch(() => {
        if (isMounted) {
          setUseGoogleMaps(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  // Render Google Maps Markers and Polylines
  useEffect(() => {
    if (!useGoogleMaps || !mapInstanceRef.current || !window.google?.maps) return;

    const maps = window.google.maps;
    const map = mapInstanceRef.current;
    const bounds = new maps.LatLngBounds();
    let hasCoords = false;

    // Clear previous markers & polylines
    for (const m of markersRef.current) {
      m.setMap(null);
    }
    markersRef.current = [];

    for (const p of polylinesRef.current) {
      p.setMap(null);
    }
    polylinesRef.current = [];

    // 1. Plot Depots
    for (const depot of depotLocations) {
      const pos = { lat: depot.lat, lng: depot.lng };
      bounds.extend(pos);
      hasCoords = true;

      const marker = new maps.Marker({
        position: pos,
        map,
        title: `Depot: ${depot.label}`,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#1e293b',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });

      marker.addListener('click', () => {
        infoWindowRef.current?.setContent(`
          <div style="font-family: sans-serif; padding: 4px;">
            <div style="font-weight: bold; font-size: 13px;">🏢 Depot Hub: ${depot.label}</div>
            <div style="font-size: 11px; color: #64748b; margin-top: 2px;">Start / End location</div>
          </div>
        `);
        infoWindowRef.current?.open(map, marker);
      });

      markersRef.current.push(marker);
    }

    // 2. Plot Unassigned Jobs (Amber markers)
    for (const job of placedUnassigned) {
      const pos = { lat: job.latitude!, lng: job.longitude! };
      bounds.extend(pos);
      hasCoords = true;

      const marker = new maps.Marker({
        position: pos,
        map,
        title: job.title,
        icon: {
          path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: '#f59e0b',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
        },
      });

      marker.addListener('click', () => {
        setSelectedJob(job);
        infoWindowRef.current?.setContent(`
          <div style="font-family: sans-serif; padding: 6px; max-width: 220px;">
            <div style="font-weight: bold; font-size: 13px; color: #0f172a;">${job.title}</div>
            <div style="font-size: 11px; color: #64748b; margin-top: 2px;">${job.formattedAddress || 'No address'}</div>
            <div style="margin-top: 6px;">
              <span style="font-size: 10px; background: #fef3c7; color: #b45309; padding: 2px 6px; border-radius: 4px; font-weight: 600;">Unassigned</span>
            </div>
          </div>
        `);
        infoWindowRef.current?.open(map, marker);
        if (onSelectJob) onSelectJob(job);
      });

      markersRef.current.push(marker);
    }

    // 3. Plot Assigned Routes & Polylines per Operator
    for (const assignment of assignments) {
      if (activeOperatorFilter !== null && assignment.operatorProfileId !== activeOperatorFilter) {
        continue;
      }

      const opColor = operatorColorMap.get(assignment.operatorProfileId) || '#3b82f6';
      const pathCoords: { lat: number; lng: number }[] = [];

      // Add start depot if available
      const opProfile = operators.find((o) => o.id === assignment.operatorProfileId);
      if (opProfile?.startLocation?.latitude && opProfile.startLocation.longitude) {
        pathCoords.push({
          lat: opProfile.startLocation.latitude,
          lng: opProfile.startLocation.longitude,
        });
      }

      // Sort stops by position
      const sortedStops = [...(assignment.stops || [])].sort((a, b) => a.position - b.position);

      for (const stop of sortedStops) {
        const job = stop.job;
        if (job && job.latitude != null && job.longitude != null) {
          const pos = { lat: job.latitude, lng: job.longitude };
          pathCoords.push(pos);
          bounds.extend(pos);
          hasCoords = true;

          const marker = new maps.Marker({
            position: pos,
            map,
            title: `Stop #${stop.position}: ${job.title}`,
            label: {
              text: String(stop.position),
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 'bold',
            },
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 13,
              fillColor: opColor,
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });

          marker.addListener('click', () => {
            setSelectedJob(job);
            const etaText =
              stop.plannedArrival != null
                ? `ETA: ${Math.floor(stop.plannedArrival / 3600)}:${String(Math.floor((stop.plannedArrival % 3600) / 60)).padStart(2, '0')}`
                : '';
            infoWindowRef.current?.setContent(`
              <div style="font-family: sans-serif; padding: 6px; max-width: 240px;">
                <div style="font-size: 11px; color: ${opColor}; font-weight: 700;">Stop #${stop.position} · ${opProfile?.user?.fullName || 'Operator'}</div>
                <div style="font-weight: bold; font-size: 13px; color: #0f172a; margin-top: 2px;">${job.title}</div>
                <div style="font-size: 11px; color: #64748b; margin-top: 2px;">${job.formattedAddress || ''}</div>
                ${etaText ? `<div style="margin-top: 6px; font-size: 10px; color: #475569;">${etaText}</div>` : ''}
              </div>
            `);
            infoWindowRef.current?.open(map, marker);
            if (onSelectJob) onSelectJob(job);
          });

          markersRef.current.push(marker);
        }
      }

      // Add end depot if available
      if (opProfile?.endLocation?.latitude && opProfile.endLocation.longitude) {
        pathCoords.push({
          lat: opProfile.endLocation.latitude,
          lng: opProfile.endLocation.longitude,
        });
      }

      // Draw polyline connecting stops (using road geometry from OSRM if available)
      const roadPath = routeGeometries.get(assignment.operatorProfileId);
      const polylineCoords = roadPath && roadPath.length > 0 ? roadPath : pathCoords;

      if (polylineCoords.length > 1) {
        const polyline = new maps.Polyline({
          path: polylineCoords,
          geodesic: true,
          strokeColor: opColor,
          strokeOpacity: 0.85,
          strokeWeight: 4,
          map,
        });
        polylinesRef.current.push(polyline);
      }
    }

    if (hasCoords && (map as { fitBounds?: (b: unknown, p?: unknown) => void }).fitBounds) {
      (map as { fitBounds: (b: unknown, p?: unknown) => void }).fitBounds(bounds, {
        top: 60,
        bottom: 60,
        left: 60,
        right: 60,
      });
    }
  }, [
    useGoogleMaps,
    placedUnassigned,
    assignments,
    depotLocations,
    operators,
    operatorColorMap,
    activeOperatorFilter,
    routeGeometries,
    onSelectJob,
  ]);

  const fitBounds = () => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    const maps = window.google.maps;
    const bounds = new maps.LatLngBounds();
    let hasCoords = false;

    for (const d of depotLocations) {
      bounds.extend({ lat: d.lat, lng: d.lng });
      hasCoords = true;
    }
    for (const j of placedUnassigned) {
      bounds.extend({ lat: j.latitude!, lng: j.longitude! });
      hasCoords = true;
    }
    for (const s of assignedStops) {
      if (s.job?.latitude && s.job.longitude) {
        bounds.extend({ lat: s.job.latitude, lng: s.job.longitude });
        hasCoords = true;
      }
    }

    if (hasCoords) {
      (mapInstanceRef.current as { fitBounds: (b: unknown, p?: unknown) => void }).fitBounds(
        bounds,
        {
          top: 60,
          bottom: 60,
          left: 60,
          right: 60,
        }
      );
    }
  };

  return (
    <div className="relative w-full h-full flex flex-col bg-muted/20 overflow-hidden rounded-xl border border-border/60">
      {/* Top Left Stats & Alerts */}
      <div className="absolute top-3 left-3 z-10 flex flex-wrap items-center gap-2 pointer-events-none">
        <Badge
          variant="secondary"
          className="bg-background/95 backdrop-blur shadow-sm text-xs gap-1.5 py-1 px-2.5 pointer-events-auto border border-border/60"
        >
          <Navigation className="h-3.5 w-3.5 text-primary" />
          <span>{assignedStops.length + placedUnassigned.length} Points Placed</span>
        </Badge>

        {missingCoordsCount > 0 && (
          <Badge
            variant="outline"
            className="bg-amber-500/10 text-amber-600 border-amber-500/30 backdrop-blur text-xs gap-1.5 py-1 px-2.5 pointer-events-auto"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{missingCoordsCount} jobs missing lat/lng</span>
          </Badge>
        )}

        {useGoogleMaps && (
          <Button
            variant="outline"
            size="sm"
            onClick={fitBounds}
            className="h-7 text-xs gap-1 bg-background/95 backdrop-blur shadow-sm pointer-events-auto"
          >
            <Maximize2 className="h-3 w-3" />
            <span>Fit Bounds</span>
          </Button>
        )}
      </div>

      {/* Top Right Operator Filter Legend */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1 bg-background/95 backdrop-blur p-3 rounded-lg border border-border/60 shadow-md max-w-xs text-xs">
        <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-1.5 mb-1">
          <span className="font-semibold text-muted-foreground text-[10px] uppercase tracking-wider">
            Operator Routes
          </span>
          {activeOperatorFilter !== null && (
            <button
              type="button"
              onClick={() => setActiveOperatorFilter(null)}
              className="text-[10px] text-primary hover:underline font-medium"
            >
              Show All
            </button>
          )}
        </div>

        {operators.map((op) => {
          const color = operatorColorMap.get(op.id) || '#3b82f6';
          const assignment = assignments.find((a) => a.operatorProfileId === op.id);
          const stopCount = assignment?.stops?.length ?? 0;
          const isFiltered = activeOperatorFilter === op.id;

          return (
            <button
              key={op.id}
              type="button"
              onClick={() => setActiveOperatorFilter((prev) => (prev === op.id ? null : op.id))}
              className={`flex items-center justify-between gap-3 text-[11px] p-1 rounded transition-colors text-left ${
                isFiltered
                  ? 'bg-primary/10 font-bold text-foreground'
                  : activeOperatorFilter !== null
                    ? 'opacity-40 hover:opacity-100'
                    : 'hover:bg-muted/40'
              }`}
            >
              <div className="flex items-center gap-2 truncate">
                <span
                  className="h-3 w-3 rounded-full shrink-0 flex items-center justify-center text-[9px] text-white font-bold"
                  style={{ backgroundColor: color }}
                />
                <span className="truncate">{op.user?.fullName || `Operator #${op.id}`}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="font-mono text-muted-foreground">{stopCount} stops</span>
                {isFiltered ? (
                  <Eye className="h-3 w-3 text-primary" />
                ) : (
                  <EyeOff className="h-3 w-3 text-muted-foreground/40" />
                )}
              </div>
            </button>
          );
        })}

        {/* Depots and Unassigned Keys */}
        <div className="pt-2 border-t border-border/40 space-y-1 text-[11px]">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-800 shrink-0" />
            <span>Depot Hubs ({depotLocations.length})</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shrink-0" />
            <span>Unassigned Jobs ({placedUnassigned.length})</span>
          </div>
        </div>
      </div>

      {/* Google Maps Container */}
      <div ref={mapContainerRef} className="w-full h-full min-h-[500px]" />

      {/* Fallback Vector Canvas View if Google Maps key is not provided */}
      {useGoogleMaps === false && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-card/90 backdrop-blur">
          <div className="max-w-md w-full flex flex-col items-center gap-3 p-6 bg-background rounded-2xl border border-border shadow-md text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-base text-foreground">Interactive Route Geometry</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Visualizing {placedUnassigned.length} unassigned jobs and {assignedStops.length}{' '}
                assigned stops across {operators.length} operator routes.
              </p>
            </div>

            <div className="w-full grid grid-cols-3 gap-2 mt-2 pt-3 border-t border-border/60 text-left text-xs">
              <div className="p-2.5 rounded-lg bg-muted/40">
                <span className="text-[10px] text-muted-foreground block">Depots</span>
                <strong className="text-sm font-semibold text-foreground">
                  {depotLocations.length}
                </strong>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/40">
                <span className="text-[10px] text-muted-foreground block">Unassigned</span>
                <strong className="text-sm font-semibold text-amber-600">
                  {placedUnassigned.length}
                </strong>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/40">
                <span className="text-[10px] text-muted-foreground block">Route Stops</span>
                <strong className="text-sm font-semibold text-emerald-600">
                  {assignedStops.length}
                </strong>
              </div>
            </div>

            {missingCoordsCount > 0 && (
              <p className="text-xs text-amber-600 bg-amber-500/10 p-2 rounded border border-amber-500/20 w-full mt-1">
                Tip: Use Google Places Autocomplete when adding jobs to automatically plot precise
                coordinates on the map.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
